import React, { useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Container,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { AiOutlineMail } from "react-icons/ai";
import { FiTruck, FiLayers } from "react-icons/fi";
import {} from "react-icons/io";

import classes from "@/styles/get-quotes/get-quotes-main.module.css";
import ChooseService from "./choose-service";
import { Address } from "@/@types/Address";
import { initialAddress } from "@/utils/constants";
import ShippingInformation from "./shipping-information";
// NEXT-GUEST-QUOTES-V2: the Parcels guest-quote path now hits the v2 endpoint
// (`POST /v2/rating/quotes/guest`). The LTL/FTL freight tabs stay on the v1 API for now —
// v2 has no freight quote endpoint yet (see Findings). Only the freight lead-capture +
// freight-rate v1 calls remain.
import { getFreightRateGuest } from "@/services/rateService";
import { getGuestQuote, mapGuestQuoteError } from "@/services/rateServiceV2";
import { mapGuestQuoteRequest } from "@/mappers/rating/v2GuestQuoteMapper";
import { mapGuestQuoteResponse } from "@/mappers/rating/v2GuestQuoteResponseMapper";
import WarningAndDisclaimer from "./warning-and-disclaimer";
import { AvailableRate, ServiceOption } from "@/@types/Rating/AvailableRate";
import { getRateResponseMapper } from "@/mappers/rating/getRatesResponseMapper";
import { uniqBy } from "@/utils/common-functions";
import { ShippingItem } from "@/@types/ShippingItem";
import useAlertBar from "@/hooks/useAlertBar";
import PackageItemsForm from "./package-items-form";
import LTLFreightItemsForm from "./ltl-freight-items-form";
import useGoogleAnalytics from "@/hooks/useGoogleAnalytics";
import FreightServiceOptions from "./freight-service-options";
import LTLQuoteModal from "./ltl-quote-modal";
import FTLFreightItemsForm from "./ftl-freight-items-form";

const GetQuotesMain = () => {
  const { openAlertBar } = useAlertBar();
  const { sendCustomEvent } = useGoogleAnalytics();
  const chooseServiceRef = useRef(null);
  const [isChooseServiceOpen, setIsChooseServiceOpen] =
    useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [availableRates, setAvailableRates] = useState<AvailableRate[]>([]);
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [cookies, setCookie] = useCookies(["formSubmitCount"]);
  const [blockRates, setBlockRates] = useState(false);
  const [ratesRequestBody, setRatesRequestBody] = useState<any>({});

  const [loading, setLoading] = useState(false);
  const [didInfoChanged, setDidInfoChanged] = useState<boolean>(false);
  const [shipFrom, setShipFrom] = useState<Address>(initialAddress);
  const [shipTo, setShipTo] = useState<Address>(initialAddress);
  const [shipDate, setShipDate] = useState<Date>(new Date());
  const [ratesWarnings, setRatesWarnings] = useState<
    { source: string; text: string }[]
  >([]);
  const [freightServiceOptions, setFreightServiceOptions] = useState<
    ServiceOption[]
  >([]);

  const getShippingAddress = () => {
    if (!shipFrom.postalCode) {
      openAlertBar("Ship from postal code is required", false);
      return;
    }

    if (!shipTo.postalCode) {
      openAlertBar("Ship to postal code is required", false);
      return;
    }

    // US ZIP Code (5-digit or 5+4 with optional '-' or space separator)
    const usZipRegex = /^\d{5}([-\s]?\d{4})?$/;

    // Canada Postal Code (A1A 1A1 or A1A1A1 format)
    const canadaPostalRegex = /^[A-Za-z]\d[A-Za-z][\s]?\d[A-Za-z]\d$/;

    if (
      shipFrom.countryCode === "US" &&
      !usZipRegex.test(shipFrom.postalCode)
    ) {
      openAlertBar("Ship from postal code is invalid", false);
      return;
    }

    if (shipTo.countryCode === "US" && !usZipRegex.test(shipTo.postalCode)) {
      openAlertBar("Ship to postal code is invalid", false);
      return;
    }

    if (
      shipFrom.countryCode === "CA" &&
      !canadaPostalRegex.test(shipFrom.postalCode)
    ) {
      openAlertBar("Ship from postal code is invalid", false);
      return;
    }

    if (
      shipTo.countryCode === "CA" &&
      !canadaPostalRegex.test(shipTo.postalCode)
    ) {
      openAlertBar("Ship to postal code is invalid", false);
      return;
    }

    return {
      shipFrom: {
        name: "ShipVista",
        streetAddress: shipFrom.streetAddress,
        streetAddres2: shipFrom.streetAddress2,
        country: shipFrom.country,
        postCode: shipFrom.postalCode.replaceAll(" ", ""),
        stateCode: shipFrom.stateCode,
        city: shipFrom.city,
        isResidence: shipFrom.isResidence,
      },
      shipTo: {
        name: "Client",
        streetAddress: shipTo.streetAddress,
        streetAddres2: shipTo.streetAddress2,
        country: shipTo.country,
        postCode: shipTo.postalCode.replaceAll(" ", ""),
        stateCode: shipTo.stateCode,
        city: shipTo.city,
        isResidence: shipTo.isResidence,
      },
      shipDate,
      serviceOptions: [],
    };
  };

  const ltlFreightFormSubmit = async ({
    freightItems,
    handlingUnit1,
    handlingUnit2,
    guestEmail,
    unitOfMeasurement,
  }: any) => {
    setLoading(true);

    // if (handlingUnit1 === 0 && handlingUnit2 === 0) {
    //   openAlertBar(
    //     `Please input the quantity of the ${handlingUnit1.name} and/or the quantity of the ${handlingUnit2.name}`,
    //     false
    //   );
    //   return;
    // }

    let ratesRequestPre: any = getShippingAddress();

    if (!ratesRequestPre) {
      setLoading(false);
      return;
    }

    ratesRequestPre["unitOfMeasurement"] = unitOfMeasurement;
    ratesRequestPre["shippingItems"] = freightItems.map(
      (item: ShippingItem) => ({
        numberOfPieces: item.quantity,
        length: item.length,
        width: item.width,
        height: item.height,
        weight: (
          parseFloat(item.weight) * parseFloat(item.quantity ?? "0")
        ).toString(),
        isDangerousGoodsIncluded: item.isDangerous,
        description: item.description,
        freightClass: item.freightClass,
        packagingTypeCode: item.packagingTypeLTL,
        insurance: item.insurance,
        InsuranceType: item.InsuranceType,
        isLTL: true,
      })
    );
    // ratesRequestPre.paymentInformation = {
    //   payerAddress: null,
    //   shippingBillingOption: "10",
    // };

    ratesRequestPre.serviceType = "EMPTY";
    ratesRequestPre.handlingUnitOne = handlingUnit1;
    ratesRequestPre.handlingUnitTwo = handlingUnit2;

    // ratesRequestPre.guestEmail = guestEmail;
    // LTL options
    if (freightServiceOptions?.length > 0) {
      freightServiceOptions.forEach((option) =>
        ratesRequestPre.serviceOptions.push(option)
      );
    }

    if (ratesRequestPre.serviceOptions.length === 0)
      ratesRequestPre.serviceOptions = null;

    try {
      sendCustomEvent("LTLGetRatesGuest", {
        page: {
          url:
            document.location.origin +
            document.location.pathname +
            document.location.search,
        },
        params: {
          from: ratesRequestPre.shipFrom,
          to: ratesRequestPre.shipTo,
        },
      });

      // if (selectedTab === 1 && shipFrom.countryCode === shipTo.countryCode) {
      //   if (shipFrom.countryCode !== "US" || shipTo.countryCode !== "US") {
      //     setFeedbackModal(true);
      //     setRatesRequestBody(ratesRequestPre);
      //     setLoading(false);
      //     return;
      //   }
      // }

      const { data } = await getFreightRateGuest(ratesRequestPre);

      setLoading(false);
      if (!data.data || data.data.length === 0) {
        openAlertBar(
          `We've had an error with: ${data.message} Please try again Later`,
          false
        );
      } else {
        if (!data.status) {
          openAlertBar(
            `We've had an error with: ${data.message} Please try again Later`,
            false
          );
        }
        const rates: any[] = getRateResponseMapper(data.data);
        setAvailableRates(rates);
        setIsChooseServiceOpen(true);
        setDidInfoChanged(false);
        (chooseServiceRef as any).current.scrollIntoView({
          behavior: "smooth",
        });
      }
      // getting the warnings
      setRatesWarnings(uniqBy(data?.warnings, JSON.stringify));
    } catch (error) {
      setLoading(false);
      openAlertBar(
        "Unexpected error occurred while submitting your request. Please contact customer care.",
        false
      );
    }
  };

  const ftlFreightFormSubmit = async ({
    freightItems,
    handlingUnit1,
    handlingUnit2,
    guestEmail,
    unitOfMeasurement,
  }: any) => {
    setLoading(true);

    // if (handlingUnit1 === 0 && handlingUnit2 === 0) {
    //   openAlertBar(
    //     `Please input the quantity of the ${handlingUnit1.name} and/or the quantity of the ${handlingUnit2.name}`,
    //     false
    //   );
    //   return;
    // }

    let ratesRequestPre: any = getShippingAddress();

    if (!ratesRequestPre) {
      setLoading(false);
      return;
    }

    ratesRequestPre["unitOfMeasurement"] = unitOfMeasurement;
    ratesRequestPre["shippingItems"] = freightItems.map(
      (item: ShippingItem) => ({
        numberOfPieces: item.quantity,
        length: item.length,
        width: item.width,
        height: item.height,
        weight: (
          parseFloat(item.weight) * parseFloat(item.quantity ?? "0")
        ).toString(),
        // isDangerousGoodsIncluded: item.isDangerous,
        // description: item.description,
        // freightClass: item.freightClass,
        // packagingTypeCode: item.packagingTypeLTL,
        // insurance: item.insurance,
        // InsuranceType: item.InsuranceType,
        isLTL: true,
        typeOfGoods: item.typeOfGoods,
        vehicleType: item.vehicleType,
      })
    );
    // ratesRequestPre.paymentInformation = {
    //   payerAddress: null,
    //   shippingBillingOption: "10",
    // };

    ratesRequestPre.serviceType = "EMPTY";
    ratesRequestPre.handlingUnitOne = handlingUnit1;
    ratesRequestPre.handlingUnitTwo = handlingUnit2;

    // ratesRequestPre.guestEmail = guestEmail;
    // LTL options
    if (freightServiceOptions?.length > 0) {
      freightServiceOptions.forEach((option) =>
        ratesRequestPre.serviceOptions.push(option)
      );
    }

    if (ratesRequestPre.serviceOptions.length === 0)
      ratesRequestPre.serviceOptions = null;

    try {
      sendCustomEvent("FTLGetRatesGuest", {
        page: {
          url:
            document.location.origin +
            document.location.pathname +
            document.location.search,
        },
        params: {
          from: ratesRequestPre.shipFrom,
          to: ratesRequestPre.shipTo,
        },
      });

      setFeedbackModal(true);
      setRatesRequestBody(ratesRequestPre);
      setLoading(false);

      // }

      // const { data } = await getFreightRateGuest(ratesRequestPre);

      // setLoading(false);
      // if (!data.data || data.data.length === 0) {
      //   openAlertBar(
      //     `We've had an error with: ${data.message} Please try again Later`,
      //     false
      //   );
      // } else {
      //   if (!data.status) {
      //     openAlertBar(
      //       `We've had an error with: ${data.message} Please try again Later`,
      //       false
      //     );
      //   }
      //   const rates: any[] = getRateResponseMapper(data.data);
      //   setAvailableRates(rates);
      //   setIsChooseServiceOpen(true);
      //   setDidInfoChanged(false);
      //   (chooseServiceRef as any).current.scrollIntoView({
      //     behavior: "smooth",
      //   });
      // }
      // // getting the warnings
      // setRatesWarnings(uniqBy(data?.warnings, JSON.stringify));
    } catch (error) {
      setLoading(false);
      openAlertBar(
        "Unexpected error occurred while submitting your request. Please contact customer care.",
        false
      );
    }
  };

  const packageFormSubmit = async ({
    shippingItems,
    unitOfMeasurement,
  }: any) => {
    setLoading(true);
    const formSubmitCount = cookies["formSubmitCount"];

    if (formSubmitCount) {
      let count = parseInt(formSubmitCount);

      if (count > 6) {
        setBlockRates(true);
        return;
      } else {
        setCookie("formSubmitCount", ++count);
      }
    } else {
      setCookie("formSubmitCount", 1);
    }

    const declaredValue =
      shippingItems.filter(
        (item: ShippingItem) =>
          item.InsuranceType?.code.toLowerCase() ===
            "insureshield".toLowerCase() && !item.insurance
      ).length > 0;
    if (declaredValue) {
      openAlertBar(
        `Please input the declared value for your insurance with InsureShield™ Coverage`,
        false
      );
      return;
    }
    let ratesRequestPre: any = getShippingAddress();

    if (!ratesRequestPre) {
      setLoading(false);
      return;
    }

    ratesRequestPre["unitOfMeasurement"] = unitOfMeasurement;
    ratesRequestPre["shippingItems"] = shippingItems.map(
      (item: ShippingItem) => ({
        quantity: item.quantity,
        length: item.length,
        width: item.width,
        height: item.height,
        weight: item.weight,
        insurance: "10",
        package: item.package,
        InsuranceType: item.InsuranceType,
      })
    );

    ratesRequestPre["NumberOfPieces"] = shippingItems
      .filter(
        (e: any) =>
          e.package.carrier === "UPS" && e.package.name === "UPS Pallet"
      )
      .reduce((e: any, ee: any) => e + parseFloat(ee.numberOfPieces), 0);

    try {
      // NEXT-GUEST-QUOTES-V2: build the v2 wire body (metric pieces, integer-cents declared
      // value, string enums) and POST /v2/rating/quotes/guest — anonymous, no auth header.
      // The v1 `200 + status:false` trap is gone: a failed quote is a real 4xx/422/502 here,
      // caught below and rendered via the friendly RFC 7807 mapper.
      const v2Body = mapGuestQuoteRequest({
        shipFrom: ratesRequestPre.shipFrom,
        shipTo: ratesRequestPre.shipTo,
        shippingItems,
        unitOfMeasurement,
      });

      const { data } = await getGuestQuote(v2Body);
      sendCustomEvent("getRatesGuest", {
        page: {
          url:
            document.location.origin +
            document.location.pathname +
            document.location.search,
        },
        params: {
          from: ratesRequestPre.shipFrom,
          to: ratesRequestPre.shipTo,
        },
      });

      setLoading(false);

      // v2 returns { quotes, carrierErrors }; the endpoint maps the all-empty cases to RFC 7807
      // (422/502) before this point, so an empty `quotes[]` here means "no rates for this lane"
      // — surface a friendly note rather than a hard error.
      const rates: AvailableRate[] = mapGuestQuoteResponse(data);
      if (rates.length === 0) {
        openAlertBar(
          "No rates were found for that route. Try a different origin, destination, or package size.",
          false
        );
        setAvailableRates([]);
      } else {
        setAvailableRates(rates);
        setIsChooseServiceOpen(true);
        setDidInfoChanged(false);
        (chooseServiceRef as any).current.scrollIntoView({
          behavior: "smooth",
        });
      }
      // v2 has no `warnings` bucket (per-carrier failures are in `carrierErrors`); the
      // warning/disclaimer surface stays empty for guest quotes.
      setRatesWarnings([]);
    } catch (error) {
      setLoading(false);
      const { message } = mapGuestQuoteError(
        error,
        "We couldn't get quotes right now. Please try again later."
      );
      openAlertBar(message, false);
    }
  };

  const hideServices = () => {
    setIsChooseServiceOpen(false);
    setDidInfoChanged(true);
  };

  useEffect(() => {
    hideServices();
  }, [selectedTab, shipTo, shipFrom, shipDate, freightServiceOptions]);

  return (
    <Container className={classes.mb8}>
      <h1 className="my-4 fs-3">Get Instant Quotes</h1>
      <ShippingInformation
        shipTo={shipTo}
        setShipTo={setShipTo}
        shipFrom={shipFrom}
        setShipFrom={setShipFrom}
        shipDate={shipDate}
        setShipDate={setShipDate}
      />
      <Card className="rounded-0 border-0 shadow-lg mb-5">
        <CardHeader className="bg-white">
          <CardTitle tag="h5">Shipping Items</CardTitle>
        </CardHeader>
        <CardBody>
          <Nav tabs>
            <NavItem>
              <NavLink
                className={
                  selectedTab === 0
                    ? "active text-darkBlue text-center fw-bold pointer p-3 px-5"
                    : "pointer text-center text-secondary p-3 px-5"
                }
                onClick={() => setSelectedTab(0)}
              >
                <AiOutlineMail size={30} />
                <br />
                Parcels (Box)
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={
                  selectedTab === 1
                    ? "active text-darkBlue fw-bold pointer text-center p-3 px-5"
                    : "pointer text-center text-secondary p-3 px-5"
                }
                onClick={() => setSelectedTab(1)}
              >
                <FiLayers size={30} />
                <br />
                LTL Freight (Pallet)
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={
                  selectedTab === 2
                    ? "active text-darkBlue fw-bold pointer text-center p-3 px-5"
                    : "pointer text-center text-secondary p-3 px-5"
                }
                onClick={() => setSelectedTab(2)}
              >
                <FiTruck size={30} />
                <br />
                FTL Freight (Container)
              </NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={selectedTab}>
            <TabPane tabId={0}>
              <PackageItemsForm
                loading={loading}
                formSubmit={packageFormSubmit}
                shipFromCountry={shipFrom?.countryCode}
                shipToCountry={shipTo?.countryCode}
                hideServices={hideServices}
                blockRates={blockRates}
              />
            </TabPane>
            <TabPane tabId={1}>
              <LTLFreightItemsForm
                loading={loading}
                formSubmit={ltlFreightFormSubmit}
                hideServices={hideServices}
                blockRates={blockRates}
              />
              <FreightServiceOptions
                selectedOptions={freightServiceOptions}
                setSelectedOptions={setFreightServiceOptions}
              />
            </TabPane>
            <TabPane tabId={2}>
              <FTLFreightItemsForm
                loading={loading}
                formSubmit={ftlFreightFormSubmit}
                hideServices={hideServices}
                blockRates={blockRates}
              />
              <FreightServiceOptions
                selectedOptions={freightServiceOptions}
                setSelectedOptions={setFreightServiceOptions}
              />
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
      <div className="my-5" ref={chooseServiceRef}>
        {isChooseServiceOpen && (
          <>
            <ChooseService
              didInfoChanged={didInfoChanged}
              availableRates={availableRates}
            />
            <WarningAndDisclaimer
              warnings={ratesWarnings}
              rates={availableRates}
            />
          </>
        )}
      </div>
      <LTLQuoteModal
        isFTL={selectedTab === 2}
        open={feedbackModal}
        toggleModal={() => setFeedbackModal((s) => !s)}
        ratesRequestBody={ratesRequestBody}
      />
      {/* <Modal
        isOpen={ltlFeedbackModal}
        toggle={() => setLtlFeedbackModal((s) => !s)}
      >
        <ModalHeader toggle={() => setLtlFeedbackModal((s) => !s)}>
          LTL Freight Shipment Request Successful
        </ModalHeader>
        <ModalBody>
          <div style={{ padding: "10px" }}>
            <p style={{ padding: "20px" }}>
              Thank you for trusting ShipVista.com to arrange your LTL Freight
              shipment! <br />A ShipVista LTL Freight team member will follow up
              with you shortly.
            </p>
            <hr />
            <div style={{ textAlign: "right" }}>
              <Button
                color="primary"
                variant="contained"
                size="small"
                onClick={() => setLtlFeedbackModal(false)}
              >
                DONE
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal> */}
    </Container>
  );
};

export default GetQuotesMain;
